import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import './PushList.css';

const PAGE_SIZE = 50;

const PushList = ({ onSelectMessage, onRefresh, refreshKey }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [sending, setSending] = useState(null); // 즉시 발송 중인 메시지 ID
  const [message, setMessage] = useState({ type: '', text: '' });
  const observerTarget = useRef(null);

  // 메시지 목록 조회
  const fetchMessages = async () => {
    setLoading(true);
    setOffset(0);
    setHasMore(true);
    try {
      const { data, error } = await supabase
        .from('push_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .range(0, PAGE_SIZE - 1);
      if (error) throw error;
      setMessages(data || []);
      setOffset(PAGE_SIZE);
      setHasMore((data?.length || 0) >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'danger', text: '메시지 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const { data, error } = await supabase
        .from('push_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      const newData = data || [];
      setMessages(prev => [...prev, ...newData]);
      setOffset(prev => prev + PAGE_SIZE);
      setHasMore(newData.length >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [refreshKey]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, offset]);

  // 즉시 발송
  const handleSendNow = async (messageId) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg) return;

    if (!window.confirm(`"${msg.title}" 메시지를 즉시 발송하시겠습니까?`)) {
      return;
    }

    setSending(messageId);
    setMessage({ type: '', text: '' });
    try {
      // Edge Function 호출
      const { data: fnData, error: fnError } = await supabase.functions.invoke('send-push', {
        body: {
          push_message_id: messageId,
          target_type: msg.target_type,
          target_user_id: msg.target_user_id,
        },
      });

      if (fnError) {
        const ctx = fnError.context;
        const status = ctx?.status ?? '';
        const detail = [status && `상태 ${status}`, ctx?.body].filter(Boolean).join(' · ');
        setMessage({
          type: 'warning',
          text: `발송 실패: ${fnError.message}${detail ? ` (${detail})` : ''}`,
        });
      } else if (fnData?.error) {
        setMessage({
          type: 'warning',
          text: `발송 실패: ${fnData.error}`,
        });
      } else {
        const success = fnData?.success_count ?? 0;
        const fail = fnData?.fail_count ?? 0;
        setMessage({
          type: 'success',
          text: `발송 완료. 성공 ${success}건, 실패 ${fail}건.`,
        });
      }

      fetchMessages();
    } catch (e) {
      console.error(e);
      setMessage({ type: 'danger', text: '발송 중 오류가 발생했습니다.' });
    } finally {
      setSending(null);
    }
  };

  const getScheduleLabel = (schedule) => {
    const map = {
      null: '잠금',
      'm': '아침',
      'a': '점심',
      'e': '저녁',
    };
    return map[schedule] || '선택';
  };

  const statusLabel = (status) => {
    const map = {
      pending: '대기',
      sent: '발송완료',
      failed: '실패',
    };
    return map[status] || status;
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3">
        <h6 className="m-0 font-weight-bold text-primary">푸시 메시지 목록</h6>
      </div>
      <div className="card-body">
        {message.text && (
          <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
            {message.text}
            <button type="button" className="close" onClick={() => setMessage({ type: '', text: '' })}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        {/* 메시지 테이블 */}
        {loading ? (
          <p className="text-muted">로드 중...</p>
        ) : messages.length === 0 ? (
          <p className="text-muted text-center py-4">메시지가 없습니다.</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered table-sm mb-0">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: '250px' }}>제목</th>
                    <th style={{ width: '100px' }}>상태</th>
                    <th style={{ width: '80px' }}>시간대</th>
                    <th style={{ width: '180px' }}>발송 시각</th>
                    <th style={{ width: '100px' }}>즉시 발송</th>
                    <th style={{ width: '180px' }}>등록 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr key={msg.id}>
                      <td>
                        <button
                          className="btn btn-link btn-sm p-0 text-start text-decoration-none"
                          onClick={() => onSelectMessage(msg.id)}
                          style={{ maxWidth: '250px' }}
                        >
                          <div className="text-truncate">{msg.title}</div>
                          <small className="text-muted text-truncate d-block">
                            {msg.body || '(내용 없음)'}
                          </small>
                        </button>
                      </td>
                      <td>
                        <small className="badge badge-secondary">{statusLabel(msg.status)}</small>
                      </td>
                      <td>
                        <small>{getScheduleLabel(msg.target_cd)}</small>
                      </td>
                      <td>
                        <small>
                          {msg.sent_at ? new Date(msg.sent_at).toLocaleString('ko-KR') : '-'}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSendNow(msg.id)}
                          disabled={sending === msg.id}
                        >
                          {sending === msg.id ? '발송 중...' : '발송'}
                        </button>
                      </td>
                      <td>
                        <small>
                          {msg.created_at ? new Date(msg.created_at).toLocaleString('ko-KR') : '-'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 스크롤 페이징 트리거 */}
            <div ref={observerTarget} className="text-center py-4">
              {loadingMore && (
                <div className="text-muted">
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  더 불러오는 중...
                </div>
              )}
              {!hasMore && messages.length > 0 && (
                <small className="text-muted">모든 데이터를 불러왔습니다.</small>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PushList;
